// @flow
import React, { useEffect, useState } from 'react';
import { isEmpty, flatten, groupBy } from 'lodash';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { NavigationUtil } from 'utils/NavigationUtil';
import { useViewport } from 'components/templates/viewportProvider';
import { RouteNames } from 'router';
import { HomeActions } from 'modules/home/actions';
import { HomeSelector } from 'modules/home/selector';
import { PageLoader } from 'components/molecules/pageLoader';
import RentalsBanner from 'components/organisms/rentalsBanner';
import { ContactUsSection } from 'components/templates/contactUsSection';
import OwnHome from 'components/templates/ownHome';
import FooterSection from 'components/templates/footer';
import PageTemplate from 'components/templates/pageTemplate';
import OurHomesDifferences from 'components/templates/ourHomesDifference';
import RentAdvertisement from 'components/organisms/rentOutHome';
import RentalBrowser from 'components/templates/rentalBrowser';
import type { AppState } from 'modules/rootReducer';
import type { Contact, SiteSettings, Vertical } from 'types/common';
import RenderSEO from 'components/atoms/renderSEO';
import { FooterSelector } from 'modules/footerLink/selector';
import { FooterActions } from 'modules/footerLink/actions';
import { LocationSelector } from 'modules/location/selector';
import { LocationActions } from 'modules/location/actions';
import { CmsPagesSelector } from 'modules/cmsPages/selector';
import { CmsDetailsSelector } from 'modules/cmsDetails/selector';
import { CmsPagesActions } from 'modules/cmsPages/actions';
import { CmsDetailsActions } from 'modules/cmsDetails/actions';
import { ProjectSelector } from 'modules/project/selector';
import { RentalActions } from 'modules/rentals/actions';
import { RentalSelector } from 'modules/rentals/selector';

type Props = {
  isLoading: boolean,
  verticals: Array<Vertical>,
  verticalId: number | null,
  getVerticals: () => void,
  siteSettings: SiteSettings,
  getSiteSettings: () => void,
  contact: Contact,
  getContactDetails: () => void,
};

const RentalsPage = (props: Props) => {
  const {
    isLoading,
    getVerticals,
    verticals,
    verticalId,
    getSiteSettings,
    siteSettings,
    contact,
    getContactDetails,
    bannerData,
    getBannerData,
    footerLink,
    getFooterLinkData,
    userLocation,
    getUserLocation,
    ourDifferenceData,
    rentalData,
    getPageData,
    getCmsDetailsData,
    cmsDetailsData,
    getRentals,
    rentalHomes,
    adData,
  } = props;

  const metaTagSEO = {
    metaTitle: 'Rentals | Total Environment Homes',
    metaDescription:
      'Rentals By Total Environment. Its time to discover your new home.Want to book a tour to new interested home? Call now at Phone:+918042453000',
    metaKeywords: '',
  };

  const { isMobile } = useViewport();
  let [theme, setTheme] = useState(isMobile ? 'dark-theme' : 'light-theme');

  useEffect(() => {
    getBannerData();
    getContactDetails();
    getVerticals();
    getSiteSettings();
    getFooterLinkData();
    getUserLocation();
    getPageData();
    getCmsDetailsData();
    getRentals('all');
  }, []);

  const [mapView, setMapView] = useState(false);

  if (isLoading) {
    return <PageLoader isMobile={isMobile} />;
  }

  if (
    !contact ||
    !siteSettings ||
    !verticals ||
    !bannerData ||
    !footerLink ||
    !cmsDetailsData ||
    !ourDifferenceData ||
    !userLocation ||
    !rentalData ||
    !rentalHomes
  ) {
    return null;
  }

  if (bannerData.metaTitle) metaTagSEO.metaTitle = bannerData.metaTitle;
  if (bannerData.metaDescription)
    metaTagSEO.metaDescription = bannerData.metaDescription;
  if (bannerData.metaKeywords)
    metaTagSEO.metaKeywords = bannerData.metaKeywords;

  const updateMapView = (theme, view) => {
    setTheme(theme);
    setMapView(view);
  };

  let projectList = flatten(rentalHomes);

  const componentList = [
    ...(!isEmpty(bannerData)
      ? [
          {
            component: RentalsBanner,
            data: bannerData,
            theme: isMobile ? 'dark-theme' : 'light-theme',
            sectionId: 'banner',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Rental Properties',
                breadcrumbLink: RouteNames.rentals,
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(projectList)
      ? [
          {
            component: RentalBrowser,
            data: {
              data: projectList,
              userLocation,
              updateMapView,
              mapView,
              type: 'rentals',
            },
            sectionId: 'projects',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Rental Properties',
                breadcrumbLink: RouteNames.homes,
              },
              {
                breadcrumbName: 'Rental Browser',
                breadcrumbLink: RouteNames.homes,
              },
            ],
            theme: isMobile ? 'dark-theme' : 'light-theme',
          },
        ]
      : []),
    ...(!isEmpty(ourDifferenceData)
      ? [
          {
            component: OurHomesDifferences,
            data: {
              ourDifferenceData,
              cmsDetailsData,
            },
            sectionId: 'our-differences',
            theme: 'light-theme',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Rental Properties',
                breadcrumbLink: RouteNames.rentals,
              },
              {
                breadcrumbName: 'Our Differences',
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(adData)
      ? [
          {
            component: RentAdvertisement,
            data: { adData, projectList },
            sectionId: 'rent-home',
            theme: 'dark-theme',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Rental Properties',
                breadcrumbLink: RouteNames.rentals,
              },
              {
                breadcrumbName: 'Rent Out Your Home',
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(rentalData)
      ? [
          {
            component: OwnHome,
            data: { rentalData },
            sectionId: 'own-home',
            theme: 'dark-theme',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Rental Properties',
                breadcrumbLink: RouteNames.rentals,
              },
              {
                breadcrumbName: 'Own A Total Environment Home',
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
    {
      component: ContactUsSection,
      data: { contact, verticals, verticalId, rental: true },
      location: props,
      sectionId: 'contact',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Rental Properties',
          breadcrumbLink: RouteNames.rentals,
        },
        {
          breadcrumbName: 'Contact Us',
          breadcrumbLink: '#',
        },
      ],
      theme: 'light-theme',
    },
    {
      component: FooterSection,
      theme: 'dark-theme',
      sectionId: 'te-links',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Rental Properties',
          breadcrumbLink: RouteNames.rentals,
        },
        {
          breadcrumbName: 'Links',
          breadcrumbLink: '#',
        },
      ],
      data: { siteSettings, footerLink },
    },
  ];

  return (
    <>
      <RenderSEO metaTags={metaTagSEO} />
      <PageTemplate
        theme={theme}
        componentList={componentList}
        updateTheme={e => setTheme(e)}
        verticals={verticals}
        siteSettings={siteSettings}
      />
    </>
  );
};

const mapStateToProps = (state: AppState, props: any) => {
  const verticalKey = NavigationUtil.getVerticalKeyForLocation(props.location);

  return {
    isLoading:
      HomeSelector.getContactsLoading(state) ||
      HomeSelector.getVerticalsLoading(state) ||
      HomeSelector.getBannerLoading(state) ||
      FooterSelector.isLoading(state) ||
      HomeSelector.getSiteSettingsLoading(state) ||
      CmsPagesSelector.isCmsPageLoading(state) ||
      LocationSelector.getUserLocationLoading(state) ||
      ProjectSelector.getProjectLoading(state) ||
      RentalSelector.getRentalLoading(state),
    contact: HomeSelector.getContactForVertical(state, verticalKey),
    verticalId: HomeSelector.getVerticalIdForKey(verticalKey),
    verticals: HomeSelector.getVerticals(state),
    footerLink: FooterSelector.getFooterData(state),
    siteSettings: HomeSelector.getSiteSettings(state),
    bannerData: HomeSelector.getBanners(state, true, 'rentals', ''),
    userLocation: LocationSelector.getUserLocation(state),
    ourDifferenceData: CmsPagesSelector.getOurDifferenceSection(
      state,
      'our-difference',
      'The Total Environment Difference'
    ),
    rentalData: CmsPagesSelector.getRentalHome(
      state,
      'Own a Total Environment Home',
      'rentals',
      'true'
    ),
    cmsDetailsData: CmsDetailsSelector.getCmsDetailsData(state),
    rentalHomes: RentalSelector.getRentals(state),
    adData: RentalSelector.getRentAdvertisement(state),
  };
};

const mapDispatchToProps = dispatch => {
  const {
    getContactDetails,
    getVerticals,
    getSiteSettings,
    getBannerData,
  } = HomeActions;
  const { getFooterLinkData } = FooterActions;
  const { getUserLocation } = LocationActions;
  const { getPageData } = CmsPagesActions;
  const { getCmsDetailsData } = CmsDetailsActions;
  const { getRentals } = RentalActions;
  return bindActionCreators(
    {
      getContactDetails,
      getVerticals,
      getSiteSettings,
      getFooterLinkData,
      getBannerData,
      getUserLocation,
      getPageData,
      getCmsDetailsData,
      getRentals,
    },
    dispatch
  );
};
export default connect(mapStateToProps, mapDispatchToProps)(RentalsPage);
